import type { Rect } from './geometry';
import { canonicalizeRegions } from './selection';
import { RasterTileStore } from './tiles';

export type ColorRangeMode = 'sampled' | 'luminosity';

export interface ColorRangeSample {
  readonly color: readonly [number, number, number];
  /** Source-pixel position the colour was picked from, used by localized range. */
  readonly x: number;
  readonly y: number;
}

export interface ColorRangeParameters {
  /** Colours that select. Empty selects nothing. */
  readonly samples: readonly ColorRangeSample[];
  /** Colours that veto a pixel when they are the closer match. */
  readonly subtracted?: readonly ColorRangeSample[];
  /** 0-255. Compared against the weighted distance below. */
  readonly fuzziness: number;
  readonly mode?: ColorRangeMode;
  /**
   * 0 disables. Otherwise coverage falls off with distance in source pixels
   * from the nearest sampled point, so one colour can be picked out of a
   * single region instead of the whole image.
   */
  readonly localizedRange?: number;
}

const luma = (r: number, g: number, b: number) => r * 0.2126 + g * 0.7152 + b * 0.0722;

/**
 * Green-weighted sRGB distance. A grey-to-grey difference of d scores exactly
 * 3d, which is why luminosity mode multiplies its own difference by three: both
 * modes then read against the same fuzziness scale.
 */
function colourDistance(r: number, g: number, b: number, sample: readonly [number, number, number], mode: ColorRangeMode): number {
  if (mode === 'luminosity') return Math.abs(luma(r, g, b) - luma(sample[0], sample[1], sample[2])) * 3;
  const red = r - sample[0];
  const green = g - sample[1];
  const blue = b - sample[2];
  return Math.sqrt(2 * red * red + 4 * green * green + 3 * blue * blue);
}

function nearest(r: number, g: number, b: number, samples: readonly ColorRangeSample[], mode: ColorRangeMode): number {
  let best = Number.POSITIVE_INFINITY;
  for (const sample of samples) {
    const distance = colourDistance(r, g, b, sample.color, mode);
    if (distance < best) best = distance;
  }
  return best;
}

/**
 * Per-pixel coverage in 0-255.
 *
 * The committed selection is binary (this editor stores selections as
 * rectangles, not coverage tiles), so the soft ramp exists to drive the dialog
 * preview and to define the fuzziness shoulder. `colorRangeRegions` selects
 * every pixel with non-zero coverage, i.e. everything strictly inside the
 * fuzziness distance.
 */
export function colorRangeCoverage(rgba: Uint8ClampedArray, parameters: ColorRangeParameters, width = 0, originX = 0, originY = 0): Uint8Array {
  const output = new Uint8Array(rgba.length / 4);
  const fuzziness = Math.max(0, Math.min(255, parameters.fuzziness));
  const mode = parameters.mode ?? 'sampled';
  const samples = parameters.samples;
  const subtracted = parameters.subtracted ?? [];
  const localized = Math.max(0, parameters.localizedRange ?? 0);
  if (samples.length === 0) return output;
  for (let index = 0; index < output.length; index += 1) {
    const offset = index * 4;
    if ((rgba[offset + 3] ?? 0) === 0) continue;
    const red = rgba[offset] ?? 0;
    const green = rgba[offset + 1] ?? 0;
    const blue = rgba[offset + 2] ?? 0;
    const distance = nearest(red, green, blue, samples, mode);
    if (subtracted.length > 0 && nearest(red, green, blue, subtracted, mode) < distance) continue;
    let amount: number;
    if (fuzziness === 0) amount = distance === 0 ? 1 : 0;
    else {
      const t = Math.max(0, Math.min(1, 1 - distance / fuzziness));
      amount = t * t * (3 - 2 * t);
    }
    if (amount <= 0) continue;
    if (localized > 0 && width > 0) {
      const x = originX + (index % width);
      const y = originY + Math.floor(index / width);
      let closest = Number.POSITIVE_INFINITY;
      for (const sample of samples) {
        const dx = x - sample.x;
        const dy = y - sample.y;
        const spatial = Math.sqrt(dx * dx + dy * dy);
        if (spatial < closest) closest = spatial;
      }
      amount *= Math.max(0, Math.min(1, 1 - closest / localized));
      if (amount <= 0) continue;
    }
    output[index] = Math.round(255 * amount);
  }
  return output;
}

/** Converts a coverage buffer into ordered, non-overlapping selection runs. */
export function coverageRegions(coverage: Uint8Array, width: number, height: number): Rect[] {
  const regions: Rect[] = [];
  for (let y = 0; y < height; y += 1) {
    let start: number | null = null;
    for (let x = 0; x <= width; x += 1) {
      const selected = x < width && (coverage[y * width + x] ?? 0) > 0;
      if (selected && start === null) start = x;
      if (!selected && start !== null) {
        regions.push({ x: start, y, width: x - start, height: 1 });
        start = null;
      }
    }
  }
  return canonicalizeRegions(regions);
}

/**
 * Coverage for a whole tile store, assembled tile by tile so the kernel never
 * needs a DOM ImageData and stays usable from the headless tooling and tests.
 */
export function storeCoverage(store: RasterTileStore, parameters: ColorRangeParameters): Uint8Array {
  const coverage = new Uint8Array(store.width * store.height);
  for (const entry of store.entries()) {
    const dimensions = store.tileDimensions(entry.x, entry.y);
    const originX = entry.x * store.tileSize;
    const originY = entry.y * store.tileSize;
    const band = colorRangeCoverage(entry.pixels, parameters, dimensions.width, originX, originY);
    for (let y = 0; y < dimensions.height; y += 1) {
      coverage.set(band.subarray(y * dimensions.width, (y + 1) * dimensions.width), (originY + y) * store.width + originX);
    }
  }
  return coverage;
}

export function colorRangeRegions(store: RasterTileStore, parameters: ColorRangeParameters): Rect[] {
  return coverageRegions(storeCoverage(store, parameters), store.width, store.height);
}
